import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { haptic } from '../lib/telegram';
import type { Product, ProductStatus } from '../types/product';

export interface UpdateProductPayload {
  name?: string;
  sku?: string | null;
  category?: string | null;
  status?: ProductStatus;
  tags?: string[];
  sortOrder?: number;
  buyLink?: string | null;
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProductPayload }) =>
      api<Product>(`/products/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: (updated) => {
      haptic.success();
      qc.setQueryData(['product', updated.id], updated);
      qc.invalidateQueries({ queryKey: ['products'] });
    },
    onError: () => haptic.error(),
  });
}

export interface CreateProductPayload {
  name: string;
  sku?: string;
  category?: string;
  sourceImageUrl: string;
  tags?: string[];
  buyLink?: string;
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateProductPayload) =>
      api<Product>('/products', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      haptic.success();
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: () => haptic.error(),
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api<void>(`/products/${id}`, { method: 'DELETE' }),
    onSuccess: (_, id) => {
      haptic.success();
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.removeQueries({ queryKey: ['product', id] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: () => haptic.error(),
  });
}

export type BulkActionKind = 'archive' | 'activate' | 'delete' | 'add_tags' | 'remove_tags';

export interface BulkActionPayload {
  ids: string[];
  action: BulkActionKind;
  tags?: string[];
}

export function useBulkAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: BulkActionPayload) =>
      api<{ count: number }>('/products/bulk-action', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      haptic.success();
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: () => haptic.error(),
  });
}

function updateProductCaches(qc: ReturnType<typeof useQueryClient>, updated: Product) {
  qc.setQueryData(['product', updated.id], updated);
  qc.invalidateQueries({ queryKey: ['products'] });
}

export function useSetProductMediaPrimary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, mediaId }: { productId: string; mediaId: string }) =>
      api<Product>(`/products/${productId}/media/${mediaId}/primary`, { method: 'POST' }),
    onSuccess: (updated) => {
      haptic.success();
      updateProductCaches(qc, updated);
    },
    onError: () => haptic.error(),
  });
}

export function useReorderProductMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, ids }: { productId: string; ids: string[] }) =>
      api<Product>(`/products/${productId}/media/reorder`, {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
    onSuccess: (updated) => {
      haptic.success();
      updateProductCaches(qc, updated);
    },
    onError: () => haptic.error(),
  });
}

export function useDeleteProductMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, mediaId }: { productId: string; mediaId: string }) =>
      api<Product>(`/products/${productId}/media/${mediaId}`, { method: 'DELETE' }),
    onSuccess: (updated) => {
      haptic.success();
      updateProductCaches(qc, updated);
    },
    onError: () => haptic.error(),
  });
}
